import { cn } from '../../utils/cn';

// Tarjeta KPI (métrica + etiqueta + hint/tendencia opcional). Portada 1:1 del primitivo Stat
// del mockup mu.
//
// Por defecto es de solo lectura. Con `onClick` pasa a ser un <button> (foco y Enter/Espacio
// nativos) para un KPI que además filtra otra vista; `pressed` lo marca como filtro activo y
// se anuncia con aria-pressed. Para una grilla de filtros de estado con conteo y barra de
// proporción, el primitivo sigue siendo KpiFilterCards.
//
// tone: neutral | success | error | warning | info | brand | reverse
// (reverse cae a sin color si tailwind.config no expone status.reverse; los
//  tokens --status-reverse existen en design-tokens.scss).

const toneAccent = {
  neutral: 'text-fg-muted bg-bg-muted',
  success: 'text-status-success bg-status-success-soft',
  error: 'text-status-error bg-status-error-soft',
  warning: 'text-status-warning bg-status-warning-soft',
  info: 'text-status-info bg-status-info-soft',
  brand: 'text-brand-700 bg-brand-50',
  reverse: 'text-status-reverse bg-status-reverse-soft',
};

const trendStyles = {
  up: 'text-status-success',
  down: 'text-status-error',
  flat: 'text-fg-muted',
};

/**
 * @param {{
 *   label: React.ReactNode, value: React.ReactNode, hint?: React.ReactNode,
 *   icon?: React.ReactNode, tone?: keyof typeof toneAccent,
 *   trend?: { direction: 'up'|'down'|'flat', label: React.ReactNode },
 *   className?: string,
 *   onClick?: () => void,
 *   pressed?: boolean,
 * }} props
 *   `pressed` solo tiene efecto con `onClick`; sin él el botón no declara aria-pressed.
 */
export function Stat({ label, value, hint, icon, tone = 'neutral', trend, className, onClick, pressed }) {
  const interactive = typeof onClick === 'function';
  // Un <button> solo admite contenido de frase: en la variante clicable los bloques internos
  // son <span>. `block` y `flex` mantienen la misma caja que tienen como <div>.
  const Box = interactive ? 'span' : 'div';
  const rootClass = cn(
    'rounded-xl border border-border bg-bg p-4 shadow-soft',
    interactive &&
      'block w-full text-left transition-all hover:shadow-card hover:border-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
    interactive && pressed && 'border-brand-500 bg-brand-50/60 hover:border-brand-500',
    className,
  );

  const body = (
    <>
      <Box className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-fg-muted uppercase tracking-wide">{label}</span>
        {icon && (
          <span className={cn('flex items-center justify-center w-8 h-8 rounded-lg', toneAccent[tone])}>
            <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>
          </span>
        )}
      </Box>
      <Box className="block mt-2 text-2xl font-semibold text-fg tabular-nums">{value}</Box>
      {(hint || trend) && (
        <Box className="mt-1 flex items-center gap-2 text-xs">
          {trend && <span className={cn('font-medium', trendStyles[trend.direction])}>{trend.label}</span>}
          {hint && <span className="text-fg-muted">{hint}</span>}
        </Box>
      )}
    </>
  );

  if (!interactive) return <div className={rootClass}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed === undefined ? undefined : !!pressed}
      className={rootClass}
    >
      {body}
    </button>
  );
}
